"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  RotateCw,
  Volume2,
  VolumeX,
  ChevronDown,
  Loader2,
  SkipBack,
} from "lucide-react";

const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

const LS_REWIND_KEY  = "audio_rewind_seconds";
const LS_FORWARD_KEY = "audio_forward_seconds";
const LS_SPEED_KEY   = "audio_default_speed";
const LS_VOLUME_KEY  = "audio_volume";

function getStoredNumber(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

function readMediaDuration(audio) {
  if (!audio) return 0;
  const d = audio.duration;
  if (Number.isFinite(d) && d > 0) return d;
  try {
    if (audio.seekable && audio.seekable.length > 0) {
      const end = audio.seekable.end(audio.seekable.length - 1);
      if (Number.isFinite(end) && end > 0) return end;
    }
  } catch { /* ignore */ }
  return 0;
}

function probeWebmDuration(audio) {
  if (!audio) return;
  if (Number.isFinite(audio.duration) && audio.duration > 0) return;
  if (audio._vbotDurationProbed) return;
  if (Number.isFinite(audio.duration) && audio.duration !== Infinity) return;
  audio._vbotDurationProbed = true;
  const wasPaused = audio.paused;
  const resumeAt = audio.currentTime || 0;
  try { audio.pause(); } catch { /* ignore */ }
  const onTimeUpdate = () => {
    if (Number.isFinite(audio.duration) && audio.duration > 0 && audio.duration !== Infinity) {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      try { audio.currentTime = Math.min(resumeAt, audio.duration); }
      catch { try { audio.currentTime = 0; } catch { /* ignore */ } }
      if (!wasPaused) audio.play().catch(() => {});
    }
  };
  audio.addEventListener("timeupdate", onTimeUpdate);
  try { audio.currentTime = 1e101; }
  catch {
    audio.removeEventListener("timeupdate", onTimeUpdate);
    audio._vbotDurationProbed = false;
    if (!wasPaused) audio.play().catch(() => {});
  }
}

export default function AudioPlaybackControls({
  audioElement = null,
  sessionId    = "",
  isPlaying    = false,
  isLoading    = false,
  onPlayPause,
  compact      = false,
}) {
  const [rewindSec,  setRewindSec]  = useState(() => getStoredNumber(LS_REWIND_KEY,  5));
  const [forwardSec, setForwardSec] = useState(() => getStoredNumber(LS_FORWARD_KEY, 5));
  const [speed,      setSpeed]      = useState(() => getStoredNumber(LS_SPEED_KEY,   1));
  const [volume,     setVolume]     = useState(() => getStoredNumber(LS_VOLUME_KEY,  1));
  const [muted,      setMuted]      = useState(false);

  const [currentTime,    setCurrentTime]    = useState(0);
  const [duration,       setDuration]       = useState(0);
  const [showSpeedMenu,  setShowSpeedMenu]  = useState(false);
  const [isBuffering,    setIsBuffering]    = useState(false);
  const [isScrubbing,    setIsScrubbing]    = useState(false);
  const [actuallyPlaying, setActuallyPlaying] = useState(false);

  const speedMenuRef = useRef(null);
  const rafRef       = useRef(null);
  const progressBarRef = useRef(null);
  const scrubbingRef = useRef(false);
  const scrubTimeRef = useRef(0);
  const wasPlayingOnScrubRef = useRef(false);
  const bufferDelayRef = useRef(null);
  const seekLockUntilRef = useRef(0);

  const scheduleBuffering = useCallback((on) => {
    if (bufferDelayRef.current) {
      clearTimeout(bufferDelayRef.current);
      bufferDelayRef.current = null;
    }
    if (!on) { setIsBuffering(false); return; }
    bufferDelayRef.current = setTimeout(() => {
      if (!scrubbingRef.current) setIsBuffering(true);
    }, 250);
  }, []);

  const commitSeek = useCallback((audio, nextTime, { resumeIfNeeded = false } = {}) => {
    if (!audio) return;
    const d = audio.duration;
    const max = Number.isFinite(d) && d > 0 ? d : Number.POSITIVE_INFINITY;
    const safe = Math.max(0, Math.min(max, nextTime));
    setCurrentTime(safe);
    seekLockUntilRef.current = Date.now() + 350;
    try { audio.currentTime = safe; }
    catch { seekLockUntilRef.current = 0; return; }
    if (resumeIfNeeded && wasPlayingOnScrubRef.current) {
      audio.play().catch(() => {});
    }
  }, []);

  const timeFromClientX = useCallback((clientX) => {
    const el = progressBarRef.current;
    if (!el || !(duration > 0)) return 0;
    const rect = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return ratio * duration;
  }, [duration]);

  useEffect(() => {
    const handler = () => {
      setRewindSec(getStoredNumber(LS_REWIND_KEY,  5));
      setForwardSec(getStoredNumber(LS_FORWARD_KEY, 5));
      setSpeed(getStoredNumber(LS_SPEED_KEY,   1));
      setVolume(getStoredNumber(LS_VOLUME_KEY,  1));
    };
    window.addEventListener("audioPrefsChanged", handler);
    return () => window.removeEventListener("audioPrefsChanged", handler);
  }, []);

  useEffect(() => {
    if (!audioElement) {
      cancelAnimationFrame(rafRef.current);
      if (bufferDelayRef.current) clearTimeout(bufferDelayRef.current);
      setCurrentTime(0);
      setDuration(0);
      setActuallyPlaying(false);
      setIsBuffering(false);
      scrubbingRef.current = false;
      setIsScrubbing(false);
      return;
    }

    const readFiniteDuration = () => readMediaDuration(audioElement);

    const syncTimeIfUnlocked = () => {
      if (scrubbingRef.current) return;
      if (Date.now() < seekLockUntilRef.current) return;
      setCurrentTime(audioElement.currentTime);
    };

    if (Number.isFinite(audioElement.currentTime)) setCurrentTime(audioElement.currentTime);

    // ⬇️ KEY FIX: never reset duration to 0 on element swap — that causes the
    // sidebar progress bar to disappear/reappear and shake the list.
    const initialDuration = readFiniteDuration();
    if (initialDuration > 0) setDuration(initialDuration);

    setActuallyPlaying(!audioElement.paused && !audioElement.ended);
    if (!(readFiniteDuration() > 0)) probeWebmDuration(audioElement);

    const onTimeUpdate  = () => {
      syncTimeIfUnlocked();
      const next = readFiniteDuration();
      if (next > 0) setDuration((prev) => (prev > 0 ? prev : next));
    };
    const onDuration    = () => {
      const next = readFiniteDuration();
      if (next > 0) setDuration(next);
      else probeWebmDuration(audioElement);
    };
    const onPlay   = () => {
      setActuallyPlaying(true);
      if (!(readFiniteDuration() > 0)) probeWebmDuration(audioElement);
    };
    const onPause  = () => setActuallyPlaying(false);
    const onEnded  = () => {
      setActuallyPlaying(false);
      if (!scrubbingRef.current) setCurrentTime(0);
      scheduleBuffering(false);
    };
    const onSeeked = () => {
      seekLockUntilRef.current = 0;
      if (!scrubbingRef.current) setCurrentTime(audioElement.currentTime);
      scheduleBuffering(false);
      const next = readFiniteDuration();
      if (next > 0) setDuration(next);
    };
    const onWaiting = () => { if (!scrubbingRef.current) scheduleBuffering(true); };
    const onPlaying = () => scheduleBuffering(false);
    const onCanPlay = () => {
      onDuration();
      if (!audioElement.seeking) scheduleBuffering(false);
    };

    audioElement.addEventListener("timeupdate",     onTimeUpdate);
    audioElement.addEventListener("loadedmetadata", onDuration);
    audioElement.addEventListener("durationchange", onDuration);
    audioElement.addEventListener("canplay",        onCanPlay);
    audioElement.addEventListener("play",           onPlay);
    audioElement.addEventListener("pause",          onPause);
    audioElement.addEventListener("ended",          onEnded);
    audioElement.addEventListener("seeked",         onSeeked);
    audioElement.addEventListener("waiting",        onWaiting);
    audioElement.addEventListener("playing",        onPlaying);

    audioElement.playbackRate = speed;
    audioElement.volume       = muted ? 0 : volume;

    return () => {
      audioElement.removeEventListener("timeupdate",     onTimeUpdate);
      audioElement.removeEventListener("loadedmetadata", onDuration);
      audioElement.removeEventListener("durationchange", onDuration);
      audioElement.removeEventListener("canplay",        onCanPlay);
      audioElement.removeEventListener("play",           onPlay);
      audioElement.removeEventListener("pause",          onPause);
      audioElement.removeEventListener("ended",          onEnded);
      audioElement.removeEventListener("seeked",         onSeeked);
      audioElement.removeEventListener("waiting",        onWaiting);
      audioElement.removeEventListener("playing",        onPlaying);
      if (bufferDelayRef.current) clearTimeout(bufferDelayRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioElement, scheduleBuffering]);

  useEffect(() => {
    if (!audioElement) return;
    const tick = () => {
      if (scrubbingRef.current || Date.now() < seekLockUntilRef.current) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      if (!audioElement.paused) {
        setCurrentTime(audioElement.currentTime);
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    if (actuallyPlaying) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(tick);
    } else {
      cancelAnimationFrame(rafRef.current);
    }
    return () => cancelAnimationFrame(rafRef.current);
  }, [audioElement, actuallyPlaying]);

  useEffect(() => { if (audioElement) audioElement.playbackRate = speed; }, [audioElement, speed]);
  useEffect(() => { if (audioElement) audioElement.volume = muted ? 0 : volume; }, [audioElement, volume, muted]);

  useEffect(() => {
    if (!showSpeedMenu) return;
    const handler = (e) => {
      if (speedMenuRef.current && !speedMenuRef.current.contains(e.target))
        setShowSpeedMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showSpeedMenu]);

  const handleRewind = useCallback((e) => {
    e.stopPropagation();
    if (!audioElement) return;
    commitSeek(audioElement, (audioElement.currentTime || 0) - rewindSec);
  }, [audioElement, rewindSec, commitSeek]);

  const handleForward = useCallback((e) => {
    e.stopPropagation();
    if (!audioElement) return;
    const d = audioElement.duration;
    const next = (audioElement.currentTime || 0) + forwardSec;
    commitSeek(audioElement, Number.isFinite(d) && d > 0 ? Math.min(d, next) : next);
  }, [audioElement, forwardSec, commitSeek]);

  const handleRestart = useCallback((e) => {
    e.stopPropagation();
    if (!audioElement) { onPlayPause?.(); return; }
    commitSeek(audioElement, 0);
    if (!audioElement.paused && !audioElement.ended) return;
    onPlayPause?.();
  }, [audioElement, onPlayPause, commitSeek]);

  const handleSpeedChange = useCallback((val) => {
    setSpeed(val);
    try { localStorage.setItem(LS_SPEED_KEY, String(val)); } catch {}
    if (audioElement) audioElement.playbackRate = val;
    setShowSpeedMenu(false);
  }, [audioElement]);

  const handleVolumeChange = useCallback((e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    setMuted(val === 0);
    try { localStorage.setItem(LS_VOLUME_KEY, String(val)); } catch {}
  }, []);

  const handleMuteToggle = useCallback((e) => {
    e.stopPropagation();
    setMuted((m) => !m);
  }, []);

  const beginScrub = useCallback((clientX) => {
    if (!audioElement || !(duration > 0)) return;
    const t = timeFromClientX(clientX);
    scrubbingRef.current = true;
    wasPlayingOnScrubRef.current = !audioElement.paused && !audioElement.ended;
    scrubTimeRef.current = t;
    setIsScrubbing(true);
    setCurrentTime(t);
    setIsBuffering(false);
    if (wasPlayingOnScrubRef.current) {
      try { audioElement.pause(); } catch { /* ignore */ }
    }
  }, [audioElement, duration, timeFromClientX]);

  const moveScrub = useCallback((clientX) => {
    if (!scrubbingRef.current || !(duration > 0)) return;
    const t = timeFromClientX(clientX);
    scrubTimeRef.current = t;
    setCurrentTime(t);
  }, [duration, timeFromClientX]);

  const endScrub = useCallback(() => {
    if (!scrubbingRef.current) return;
    scrubbingRef.current = false;
    setIsScrubbing(false);
    commitSeek(audioElement, scrubTimeRef.current, { resumeIfNeeded: true });
    wasPlayingOnScrubRef.current = false;
  }, [audioElement, commitSeek]);

  useEffect(() => {
    if (!isScrubbing) return;
    const onMove = (e) => {
      const clientX = e.touches?.[0]?.clientX ?? e.clientX;
      if (typeof clientX === "number") moveScrub(clientX);
    };
    const onUp = () => endScrub();
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("touchmove", onMove, { passive: true });
    document.addEventListener("touchend", onUp);
    document.addEventListener("touchcancel", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onUp);
      document.removeEventListener("touchcancel", onUp);
    };
  }, [isScrubbing, moveScrub, endScrub]);

  const handleProgressPointerDown = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    const clientX = e.touches?.[0]?.clientX ?? e.clientX;
    if (typeof clientX === "number") beginScrub(clientX);
  }, [beginScrub]);

  const handlePlayPauseClick = useCallback((e) => {
    e.stopPropagation();
    onPlayPause?.();
  }, [onPlayPause]);

  const fmt = (s) => {
    if (!Number.isFinite(s) || s < 0) return "0:00";
    const m   = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const showPause = actuallyPlaying || isPlaying;
  const progress  = duration > 0 ? (currentTime / duration) * 100 : 0;
  const hasAudio  = !!audioElement;
  const showBusy  = isLoading || (isBuffering && !isScrubbing);

  // ──────────────────────────────────────────────────────────────
  // COMPACT VARIANT
  // NOTE: progress bar + time row are ALWAYS rendered (never gated
  // by `hasSeekableDuration`) so the container height is constant.
  // This is the primary fix for the sidebar "shake".
  // ──────────────────────────────────────────────────────────────
  if (compact) {
    return (
      <div
        className="flex flex-col gap-1.5 w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Seek bar — height is FIXED at h-1.5 to prevent scrub-time shift */}
        <div
          ref={progressBarRef}
          className="relative h-1.5 bg-gray-300 rounded-full cursor-pointer group touch-none select-none"
          onMouseDown={handleProgressPointerDown}
          onTouchStart={handleProgressPointerDown}
          role="slider"
          aria-valuemin={0}
          aria-valuemax={duration}
          aria-valuenow={currentTime}
          aria-label="Audio progress"
        >
          <div
            className="h-full bg-blue-500 rounded-full transition-none"
            style={{ width: `${progress}%` }}
          />
          <div
            className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white border-2 border-blue-500 rounded-full shadow transition-opacity pointer-events-none ${
              isScrubbing ? "opacity-100" : "opacity-0 group-hover:opacity-100"
            }`}
            style={{ left: `calc(${progress}% - 6px)` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-400 tabular-nums">
          <span>{fmt(currentTime)}</span>
          <span>{fmt(duration)}</span>
        </div>

        {/* Controls row */}
        <div className="flex items-center justify-between gap-1 w-full mt-1">
          <button
            onClick={handleRewind}
            disabled={!hasAudio}
            title={`Rewind ${rewindSec}s`}
            className="flex flex-col items-center justify-center flex-1 text-gray-500 hover:text-blue-600 disabled:opacity-30 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span className="text-xs leading-none mt-0.5">{rewindSec}s</span>
          </button>

          <button
            onClick={handlePlayPauseClick}
            disabled={isLoading}
            title={showPause ? "Pause" : "Play"}
            className="flex flex-col items-center justify-center flex-1 text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-wait transition-colors"
          >
            {showBusy ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : showPause ? (
              <Pause className="w-3.5 h-3.5" />
            ) : (
              <Play className="w-3.5 h-3.5" />
            )}
            <span className="text-xs leading-none mt-0.5 font-medium">
              {showBusy ? "..." : showPause ? "Pause" : "Play"}
            </span>
          </button>

          <button
            onClick={handleForward}
            disabled={!hasAudio}
            title={`Forward ${forwardSec}s`}
            className="flex flex-col items-center justify-center flex-1 text-gray-500 hover:text-blue-600 disabled:opacity-30 transition-colors"
          >
            <RotateCw className="w-3.5 h-3.5" />
            <span className="text-xs leading-none mt-0.5">{forwardSec}s</span>
          </button>

          <button
            onClick={handleRestart}
            disabled={!hasAudio}
            title="Restart from beginning"
            className="flex flex-col items-center justify-center flex-1 text-gray-500 hover:text-blue-600 disabled:opacity-30 transition-colors"
          >
            <SkipBack className="w-3.5 h-3.5" />
            <span className="text-xs leading-none mt-0.5">Start</span>
          </button>

          <button
            onClick={handleMuteToggle}
            title={muted ? "Unmute" : "Mute"}
            className="flex flex-col items-center justify-center flex-1 text-gray-500 hover:text-blue-600 transition-colors"
          >
            {muted ? (
              <VolumeX className="w-3.5 h-3.5" />
            ) : (
              <Volume2 className="w-3.5 h-3.5" />
            )}
            <span className="text-xs leading-none mt-0.5">
              {muted ? "Unmute" : "Mute"}
            </span>
          </button>
        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────
  // FULL VARIANT
  // ──────────────────────────────────────────────────────────────
  return (
    <div
      className="flex flex-col gap-3 w-full bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex flex-col gap-1">
        <div
          ref={progressBarRef}
          className="relative h-2 bg-gray-100 rounded-full cursor-pointer group overflow-visible touch-none select-none"
          onMouseDown={handleProgressPointerDown}
          onTouchStart={handleProgressPointerDown}
          role="slider"
          aria-valuemin={0}
          aria-valuemax={duration}
          aria-valuenow={currentTime}
          aria-label="Audio progress"
        >
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-none"
            style={{ width: `${progress}%` }}
          />
          <div
            className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-blue-500 rounded-full shadow-md transition-opacity pointer-events-none ${
              isScrubbing ? "opacity-100" : "opacity-0 group-hover:opacity-100"
            }`}
            style={{ left: `calc(${progress}% - 8px)` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-400 tabular-nums px-0.5">
          <span>{fmt(currentTime)}</span>
          <span>{fmt(duration)}</span>
        </div>
      </div>

      <div className="flex items-center justify-center gap-4">
        <button
          onClick={handleRewind}
          disabled={!hasAudio}
          title={`Rewind ${rewindSec}s`}
          className="flex flex-col items-center gap-0.5 text-gray-500 hover:text-blue-600 disabled:opacity-30 transition-colors group"
        >
          <RotateCcw className="w-5 h-5 group-hover:-rotate-12 transition-transform" />
          <span className="text-xs font-medium leading-none">{rewindSec}s</span>
        </button>

        <button
          onClick={handlePlayPauseClick}
          disabled={isLoading}
          className="w-11 h-11 flex items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 active:scale-95 disabled:opacity-50 disabled:cursor-wait transition-all shadow-md hover:shadow-lg"
        >
          {showBusy ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : showPause ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5 ml-0.5" />
          )}
        </button>

        <button
          onClick={handleForward}
          disabled={!hasAudio}
          title={`Forward ${forwardSec}s`}
          className="flex flex-col items-center gap-0.5 text-gray-500 hover:text-blue-600 disabled:opacity-30 transition-colors group"
        >
          <RotateCw className="w-5 h-5 group-hover:rotate-12 transition-transform" />
          <span className="text-xs font-medium leading-none">{forwardSec}s</span>
        </button>

        <button
          onClick={handleRestart}
          disabled={!hasAudio}
          title="Restart from beginning"
          className="flex flex-col items-center gap-0.5 text-gray-500 hover:text-blue-600 disabled:opacity-30 transition-colors"
        >
          <SkipBack className="w-5 h-5" />
          <span className="text-xs font-medium leading-none">Start</span>
        </button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative" ref={speedMenuRef}>
          <button
            onClick={(e) => { e.stopPropagation(); setShowSpeedMenu((v) => !v); }}
            className="flex items-center gap-1 text-sm font-semibold text-gray-700 hover:text-blue-600 bg-gray-100 hover:bg-blue-50 px-3 py-1.5 rounded-lg border border-gray-200 hover:border-blue-200 transition-all"
          >
            {speed}x
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showSpeedMenu ? "rotate-180" : ""}`} />
          </button>
          {showSpeedMenu && (
            <div className="absolute bottom-full mb-2 left-0 bg-white border border-gray-200 rounded-xl shadow-xl py-1.5 z-50 min-w-[90px] overflow-hidden">
              <p className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">Speed</p>
              {SPEED_OPTIONS.map((s) => (
                <button
                  key={s}
                  onClick={(e) => { e.stopPropagation(); handleSpeedChange(s); }}
                  className={`w-full text-left text-sm py-1.5 px-3 hover:bg-blue-50 transition-colors flex items-center justify-between ${
                    speed === s ? "text-blue-600 font-semibold" : "text-gray-700"
                  }`}
                >
                  <span>{s}x</span>
                  {s === 1 && <span className="text-xs text-gray-400">Normal</span>}
                  {speed === s && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-1">
          <button
            onClick={handleMuteToggle}
            className="text-gray-500 hover:text-blue-600 transition-colors flex-shrink-0"
            title={muted ? "Unmute" : "Mute"}
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <input
            type="range" min="0" max="1" step="0.05"
            value={muted ? 0 : volume}
            onChange={handleVolumeChange}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 h-1.5 accent-blue-600 cursor-pointer rounded-full"
            title={`Volume: ${Math.round((muted ? 0 : volume) * 100)}%`}
          />
          <span className="text-xs text-gray-400 tabular-nums w-7 text-right">
            {Math.round((muted ? 0 : volume) * 100)}%
          </span>
        </div>
      </div>
    </div>
  );
}